/* Ascend — Halloween seasonal layer (behaviour).
 * Decorative only. Mounts the homepage hero show and the header spider. When motion is allowed it:
 *  - starts the ambient CSS loops after load (body.hw-motion) — GPU-composited transforms/opacity on spans;
 *  - flies the witch with the Web Animations API (compositor-run, so nothing wakes the main thread);
 *  - lazy-loads GSAP only for one-shot choreography: the bat burst (once per visit) and the ghost peeks
 *    (desktop) — GSAP's ticker sleeps again as soon as those finish.
 * Never animates for prefers-reduced-motion; the pause control (WCAG 2.2.2) is remembered; loops stop while
 * their part of the page is off screen; nothing ever covers a control.
 * The pause control also covers the Monster Gym section (monster-gym.js, GSAP: paused via the global timeline), the
 * Halloween product drop and the graveyard band (CSS loops under .hw-motion, stopped by .hw-paused). */
(function () {
  'use strict';

  var body = document.body;
  if (!body || !body.classList.contains('hw-on')) return;

  var cfg = window.AscendHalloween || {};
  function media(query) {
    return window.matchMedia ? window.matchMedia(query) : { matches: false };
  }
  var reduceMQ = media('(prefers-reduced-motion: reduce)');
  var desktopMQ = media('(min-width: 990px)');
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var PAUSE_KEY = 'ascend-hw-paused';
  var BURST_KEY = 'ascend-hw-bats';

  // Storage can be blocked (private mode, site-data settings): never let that break the page.
  function read(key, storeName) {
    try { var s = window[storeName || 'localStorage']; return s ? s.getItem(key) : null; } catch (e) { return null; }
  }
  function write(key, value, storeName) {
    try { var s = window[storeName || 'localStorage']; if (s) s.setItem(key, value); } catch (e) { /* blocked */ }
  }
  function debounce(fn, ms) {
    var t;
    return function () { window.clearTimeout(t); t = window.setTimeout(fn, ms); };
  }
  function observe(el, options, onChange) {
    if (!el) return null;
    if (!('IntersectionObserver' in window)) { onChange(true); return null; }
    var io = new IntersectionObserver(function (entries) {
      onChange(entries[entries.length - 1].isIntersecting);
    }, options);
    io.observe(el);
    return io;
  }

  // A cached page that outlived the "hide after" date shows nothing.
  if (cfg.lastDay) {
    var now = new Date();
    var today = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
    if (today > cfg.lastDay) {
      body.classList.remove('hw-on');
      body.classList.add('hw-expired');
      return;
    }
  }

  /* ---------- Mount (DOMContentLoaded; everything is absolutely positioned → no layout shift) ---------- */

  function mountHero() {
    if (!cfg.isHome) return null;
    var slideshow = document.querySelector('#MainContent slideshow-component');
    var host = slideshow && slideshow.closest('.shopify-section');
    if (!host) return null;
    var existing = host.querySelector('.hw-hero');
    if (existing) return existing;
    var tpl = document.getElementById('hw-hero-tpl');
    if (!tpl || !('content' in tpl)) return null;
    host.classList.add('hw-hero-host');
    host.appendChild(tpl.content.cloneNode(true));
    return host.querySelector('.hw-hero');
  }

  var spiderCopy = null;
  var spiderIO = null;
  function mountSpider(heroEl) {
    if (spiderIO) { spiderIO.disconnect(); spiderIO = null; }
    var spider = document.querySelector('.hw-spider');
    if (spider && !spiderCopy) spiderCopy = spider.cloneNode(true);
    var header = document.querySelector('.section-header');
    if (!heroEl || !desktopMQ.matches || !header) {
      if (spider && spider.parentNode) spider.parentNode.removeChild(spider);
      return null;
    }
    if (!spider && spiderCopy) spider = spiderCopy.cloneNode(true);
    if (!spider) return null;
    spider.classList.add('hw-spider--away');
    header.appendChild(spider);
    spider.hidden = false;
    // The spider only hangs around while the hero is on screen, so it never sits over products or buttons.
    spiderIO = observe(heroEl, { rootMargin: '-340px 0px 0px 0px', threshold: 0 }, function (near) {
      spider.classList.toggle('hw-spider--away', !near);
    });
    return spider;
  }

  var hero = mountHero();
  var spider = mountSpider(hero);
  var toggle = document.getElementById('hw-toggle');
  window.requestAnimationFrame(function () { body.classList.add('hw-ready'); });

  /* ---------- State ---------- */

  var started = false;
  var userPaused = read(PAUSE_KEY) === '1';
  var heroVisible = false;
  var graveyardVisible = false;
  var monsterGym = document.querySelector('.mg');
  var monsterGymVisible = false;
  var drop = document.querySelector('.hd');
  var dropVisible = false;
  var heroIO = null;
  var flights = []; // looping hero animations: { pause(), resume(), stop() }

  function sync() {
    var run = heroVisible && !userPaused;
    if (hero) hero.classList.toggle('hw-offscreen', !heroVisible);
    flights.forEach(function (f) { if (run) f.resume(); else f.pause(); });
    // The pause button is only needed while something keeps moving: the hero, the Monster Gym, the product drop or the
    // graveyard.
    if (toggle) toggle.classList.toggle('hw-toggle--away', !((hero && heroVisible) || monsterGymVisible || dropVisible || graveyardVisible));
  }

  function setPaused(paused, remember) {
    userPaused = paused;
    body.classList.toggle('hw-paused', paused);
    if (toggle) toggle.setAttribute('aria-pressed', String(paused));
    if (remember) write(PAUSE_KEY, paused ? '1' : '0');
    cfg.paused = paused;
    try { document.dispatchEvent(new CustomEvent('hw:pause', { detail: { paused: paused } })); } catch (e) { /* old browsers */ }
    if (window.gsap) {
      if (paused) window.gsap.globalTimeline.pause();
      else window.gsap.globalTimeline.resume();
    }
    sync();
  }

  function pauseControl() {
    if (!toggle) return;
    toggle.hidden = false;
    toggle.addEventListener('click', function () { setPaused(!userPaused, true); });
    setPaused(userPaused, false);
  }

  function watchHero() {
    if (heroIO) { heroIO.disconnect(); heroIO = null; }
    if (!hero) return;
    heroIO = observe(hero, { threshold: 0 }, function (visible) { heroVisible = visible; sync(); });
  }

  // The graveyard band (fog drift; phones pan across the stones) only moves while it's on screen.
  function watchGraveyard() {
    var graveyard = document.querySelector('.gy');
    observe(graveyard, { threshold: 0 }, function (visible) {
      graveyard.classList.toggle('gy--live', visible);
      graveyardVisible = visible;
      sync();
    });
  }

  // The product drop (fog drift, the ghosts fading in and out) only moves while it's on screen.
  var dropIO = null;
  function watchDrop() {
    if (dropIO) { dropIO.disconnect(); dropIO = null; }
    var el = drop;
    dropIO = observe(el, { threshold: 0 }, function (visible) {
      el.classList.toggle('hd--live', visible);
      dropVisible = visible;
      sync();
    });
  }

  var monsterGymIO = null;
  function watchMonsterGym() {
    if (monsterGymIO) { monsterGymIO.disconnect(); monsterGymIO = null; }
    monsterGymIO = observe(monsterGym, { threshold: 0 }, function (visible) { monsterGymVisible = visible; sync(); });
  }

  /* ---------- The witch: rests on the moon, takes off right, sweeps back in from the left, lands ---------- */

  function witchFlight(stage) {
    var witch = stage.querySelector('.hw-witch');
    if (!witch || !witch.animate) return null;
    var anim = null;
    var paused = true;
    function build() {
      var keep = anim ? anim.currentTime : null;
      if (anim) anim.cancel();
      var right = stage.clientWidth - witch.offsetLeft + 40;
      var left = -(witch.offsetLeft + witch.offsetWidth + 40);
      anim = witch.animate([
        { offset: 0, transform: 'translate(0, 0) rotate(0deg)', easing: 'cubic-bezier(.55,.085,.68,.53)' },
        { offset: 0.188, transform: 'translate(' + right + 'px, -12px) rotate(6deg)' },
        { offset: 0.188, transform: 'translate(' + left + 'px, 30px) rotate(-4deg)', easing: 'cubic-bezier(.39,.575,.565,1)' },
        { offset: 0.529, transform: 'translate(0, 0) rotate(0deg)' },
        { offset: 1, transform: 'translate(0, 0) rotate(0deg)' }
      ], { duration: 19100, delay: 4000, iterations: Infinity });
      if (keep !== null) anim.currentTime = keep;
      if (paused) anim.pause();
    }
    build();
    var onResize = debounce(build, 200);
    window.addEventListener('resize', onResize, { passive: true });
    return {
      pause: function () { paused = true; if (anim) anim.pause(); },
      resume: function () { paused = false; if (anim) anim.play(); },
      stop: function () { window.removeEventListener('resize', onResize); if (anim) anim.cancel(); }
    };
  }

  /* ---------- GSAP one-shots (lazy-loaded) ---------- */

  function loadScript(src, onload) {
    var script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = onload;
    document.head.appendChild(script);
  }
  var gsapWaiting = null;
  function loadGsap(done) {
    if (window.gsap) { done(); return; }
    if (!cfg.gsapSrc) return;
    if (gsapWaiting) { gsapWaiting.push(done); return; }
    gsapWaiting = [done];
    loadScript(cfg.gsapSrc, function () {
      var queue = gsapWaiting;
      gsapWaiting = null;
      queue.forEach(function (fn) { try { fn(); } catch (e) { /* keep going */ } });
    });
  }

  /* ---------- Monster Gym (homepage section): its scene script loads once, after GSAP ---------- */
  var monsterGymLoading = false;
  function startMonsterGym() {
    if (!monsterGym || !cfg.mgSrc) return;
    if (window.AscendMonsterGym) { window.AscendMonsterGym.init(monsterGym); return; }
    if (monsterGymLoading) return;
    monsterGymLoading = true;
    loadScript(cfg.mgSrc, function () {
      if (window.AscendMonsterGym && monsterGym) window.AscendMonsterGym.init(monsterGym);
    });
  }

  // Bats burst out of the moon once per visit. Geometry is measured before anything is written.
  function batBurst(plan) {
    var gsap = window.gsap;
    var random = gsap.utils.random;
    plan.bats.forEach(function (item, i) {
      gsap.from(item.el, {
        x: item.dx, y: item.dy, scale: 0.15, autoAlpha: 0,
        duration: random(1.3, 2), delay: 0.3 + i * 0.12, ease: 'power3.out'
      });
    });
    write(BURST_KEY, '1', 'sessionStorage');
  }

  function measureBurst(stage) {
    var moon = stage.querySelector('.hw-moon');
    var stageBox = stage.getBoundingClientRect();
    var onScreen = stageBox.bottom > 0 && stageBox.top < window.innerHeight;
    if (!moon || !onScreen) return null;
    var m = moon.getBoundingClientRect();
    var mx = m.left + m.width / 2;
    var my = m.top + m.height / 2;
    var bats = Array.prototype.slice.call(stage.querySelectorAll('.hw-bat')).map(function (el) {
      var b = el.getBoundingClientRect();
      return { el: el, w: b.width, dx: mx - (b.left + b.width / 2), dy: my - (b.top + b.height / 2) };
    }).filter(function (item) { return item.w > 0; }); // bats hidden on this screen size
    return { bats: bats };
  }

  function ghostPeeks() {
    var ghost = document.querySelector('.hw-peeker');
    if (!ghost) return;
    if (!desktopMQ.matches || !('IntersectionObserver' in window)) {
      if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      return;
    }
    var gsap = window.gsap;
    gsap.set(ghost, { x: 0, xPercent: 100 });
    var shown = 0;
    var sections = Array.prototype.slice.call(document.querySelectorAll('#MainContent > .shopify-section'))
      .filter(function (s) { return !s.classList.contains('hw-hero-host') && !s.querySelector('.mg, .hd'); })
      .slice(0, 6);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        if (userPaused || shown >= 3) return;
        shown += 1;
        gsap.timeline()
          .to(ghost, { xPercent: 30, duration: 0.55, ease: 'back.out(1.6)' })
          .to(ghost, { rotation: -8, duration: 0.22, yoyo: true, repeat: 3, ease: 'sine.inOut' })
          .to(ghost, { xPercent: 100, duration: 0.4, ease: 'power2.in' }, '+=0.9');
      });
    }, { rootMargin: '0px 0px -45% 0px', threshold: 0 });
    sections.forEach(function (section) { io.observe(section); });
  }

  /* ---------- Theme editor: sections re-render in place, so put the decorations back ---------- */

  if (designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      try {
        var section = event.target;
        var gym = section.querySelector && section.querySelector('.mg');
        if (gym) {
          monsterGym = gym;
          monsterGymVisible = false;
          if (started) { watchMonsterGym(); loadGsap(startMonsterGym); }
          sync();
          return;
        }
        var dropEl = section.querySelector && section.querySelector('.hd');
        if (dropEl) {
          drop = dropEl;
          dropVisible = false;
          if (started) watchDrop();
          sync();
          return;
        }
        var isSlideshow = cfg.isHome && section.querySelector && section.querySelector('slideshow-component');
        var isHeader = section.classList && section.classList.contains('section-header');
        if (!isSlideshow && !isHeader) return;
        if (isSlideshow) {
          flights.forEach(function (f) { f.stop(); });
          flights = [];
          hero = mountHero();
        }
        spider = mountSpider(hero);
        if (started && hero) {
          watchHero();
          var flight = witchFlight(hero);
          if (flight) flights.push(flight);
        }
        sync();
      } catch (e) { /* editor only */ }
    });
    document.addEventListener('shopify:section:unload', function (event) {
      try {
        var gone = event.target.querySelector && event.target.querySelector('.hd');
        if (gone && gone === drop) {
          if (dropIO) { dropIO.disconnect(); dropIO = null; }
          drop = null;
          dropVisible = false;
          sync();
          return;
        }
        var gym = event.target.querySelector && event.target.querySelector('.mg');
        if (!gym) return;
        if (window.AscendMonsterGym) window.AscendMonsterGym.destroy(gym);
        if (gym === monsterGym) { monsterGym = null; monsterGymVisible = false; sync(); }
      } catch (e) { /* editor only */ }
    });
  }

  /* ---------- Start after load, when the browser is idle ---------- */

  var motionAllowed = cfg.motion !== false && !reduceMQ.matches;
  // Phones only animate the homepage (hero show, Monster Gym, graveyard); other pages stay still on phones
  // (and need no pause button).
  if (!motionAllowed || (!hero && !monsterGym && !drop && !desktopMQ.matches)) {
    body.classList.add('hw-static');
    return;
  }

  function whenIdle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 2500 });
    else window.setTimeout(fn, 300);
  }
  function afterLoad(fn) {
    if (document.readyState === 'complete') whenIdle(fn);
    else window.addEventListener('load', function () { whenIdle(fn); }, { once: true });
  }

  afterLoad(function start() {
    started = true;
    // Measure first (no writes have happened yet), then start everything.
    var burstPlan = null;
    if (hero && !userPaused && !designMode && read(BURST_KEY, 'sessionStorage') !== '1') {
      try { burstPlan = measureBurst(hero); } catch (e) { burstPlan = null; }
    }

    try { pauseControl(); } catch (e) { /* keep going */ }
    body.classList.add('hw-motion');
    try { watchHero(); } catch (e) { /* keep going */ }
    try { watchGraveyard(); } catch (e) { /* keep going */ }
    try { watchMonsterGym(); } catch (e) { /* keep going */ }
    try { watchDrop(); } catch (e) { /* keep going */ }
    try {
      var flight = hero ? witchFlight(hero) : null;
      if (flight) flights.push(flight);
    } catch (e) { /* the witch stays on the moon */ }
    sync();

    // GSAP is only fetched when there's something to play: this visit's bat burst, desktop ghost peeks, or the
    // Monster Gym scene (its script loads after GSAP; the pause button exists by now, so nothing moves without it).
    if ((burstPlan && burstPlan.bats.length) || desktopMQ.matches || monsterGym) {
      loadGsap(function () {
        try { if (burstPlan && burstPlan.bats.length) batBurst(burstPlan); } catch (e) { /* bats stay put */ }
        try { ghostPeeks(); } catch (e) { /* no peeks */ }
        body.classList.add('hw-gsap');
        setPaused(userPaused, false);
        try { startMonsterGym(); } catch (e) { /* the gym stays still */ }
      });
    }

    if (reduceMQ.addEventListener) {
      reduceMQ.addEventListener('change', function (e) { if (e.matches) setPaused(true, false); });
    }
  });
})();
